import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useRef, useState, type FC } from "react";
import { useFormContext } from "react-hook-form";
import { useDialog } from "../../../utils/hooks/use-dialog";
import type { SerialisedEditorDesign } from "../../message/editor/types/serialised-editor-design";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import {
  useImportableCommunicationContent,
  useImportableCommunications,
  type ImportableCommunication,
} from "../hooks/use-importable-communications";

export const WhatsAppContentImport: FC<{
  disabled: boolean;
  onImport: (design: SerialisedEditorDesign) => void;
}> = ({ disabled, onImport }) => {
  const dialog = useDialog();
  const [selected, setSelected] = useState<ImportableCommunication | null>(null);
  const [content, setContent] = useState<SerialisedEditorDesign | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState(false);
  const selectionRequest = useRef(0);
  const { getValues } = useFormContext<MomentFormMoment>();
  const { data: communications, loading, error } = useImportableCommunications(getValues("id"));
  const loadContent = useImportableCommunicationContent();

  const label = (communication: ImportableCommunication): string =>
    `${communication.title} — ${communication.channel === "whatsapp" ? "WhatsApp" : "Email"}`;

  const handleImport = (): void => {
    if (!content) return;

    onImport(content);
    handleClose();
  };

  const handleClose = (): void => {
    selectionRequest.current++;
    setSelected(null);
    setContent(null);
    setContentLoading(false);
    setContentError(false);
    dialog.handleClose();
  };

  const handleSelection = async (value: ImportableCommunication | null): Promise<void> => {
    const request = ++selectionRequest.current;
    setSelected(value);
    setContent(null);
    setContentError(false);

    if (!value) {
      setContentLoading(false);
      return;
    }

    setContentLoading(true);
    try {
      const loadedContent = await loadContent(value.id);
      if (request === selectionRequest.current && loadedContent) setContent(loadedContent);
    } catch {
      if (request === selectionRequest.current) setContentError(true);
    } finally {
      if (request === selectionRequest.current) setContentLoading(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        disabled={disabled}
        onClick={dialog.handleOpen}
        data-analytics-id="whatsapp-import-content"
      >
        Import content from existing Communication
      </Button>
      <Dialog open={dialog.open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Import content from existing Communication</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography>
              Select an email or WhatsApp Communication. Importing will replace the content
              currently in this page.
            </Typography>
            {loading && <CircularProgress aria-label="Loading Communications" />}
            {error && (
              <Alert severity="error">
                Unable to load Communications. Close the importer and try again.
              </Alert>
            )}
            {!loading && !error && (
              <Autocomplete
                options={communications ?? []}
                value={selected}
                getOptionLabel={label}
                getOptionKey={(option) => option.id}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) => void handleSelection(value)}
                noOptionsText="No email or WhatsApp Communications are available"
                renderInput={(params) => <TextField {...params} label="Communication" />}
              />
            )}
            {contentLoading && <CircularProgress aria-label="Loading Communication content" />}
            {contentError && (
              <Alert severity="error">
                Unable to load this Communication&apos;s content. Select another or close and try
                again.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={handleClose}
            data-analytics-id="whatsapp-import-content-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            disabled={!selected || !content || contentLoading}
            onClick={handleImport}
            data-analytics-id="whatsapp-import-content-confirm"
          >
            Import content
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
